import { create } from 'zustand';
import { applicationService } from 'src/services/application';
import { useApplicationStore } from './application';
import { useInsuranceStore } from './insurance';

interface ArchivedPlansState {
  isModalOpen: boolean;
  isProcessing: boolean;
  error: string | null;
  hasChecked: boolean;

  setModalOpen: (isOpen: boolean) => void;
  resetArchivedPlans: () => Promise<void>;
  checkArchivedPlans: () => boolean;
}

export const useArchivedPlansStore = create<ArchivedPlansState>((set, get) => ({
  isModalOpen: false,
  isProcessing: false,
  error: null,
  hasChecked: false,

  setModalOpen: (isOpen: boolean) => {
    set({ isModalOpen: isOpen });
  },

  resetArchivedPlans: async () => {
    set({ isProcessing: true, error: null });
    try {
      const { currentApplicationId } = useApplicationStore.getState();
      if (!currentApplicationId) {
        throw new Error('No application ID found');
      }
      await applicationService.resetArchivedPlans(currentApplicationId);
      await useApplicationStore.getState().updateTierFromApplicationState();
      await useApplicationStore.getState().getCurrentApplication();
      set({ isModalOpen: false, isProcessing: false, hasChecked: false });
      window.location.reload();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.details?.message || 'Failed to reset archived plans';
      console.error('Failed to reset archived plans:', error);
      set({ error: errorMessage, isProcessing: false });
      throw error;
    }
  },

  checkArchivedPlans: () => {
    // Only check once per page load
    if (get().hasChecked) return get().isModalOpen;

    const { originalApiPlans } = useApplicationStore.getState();
    const fullPlans = useInsuranceStore.getState().plans;

    if (!originalApiPlans.length || !fullPlans.length) return false;

    set({ hasChecked: true });

    const archivedPlans = originalApiPlans.filter((appPlan) => {
      const fullPlan = fullPlans.find((p) => p.id === appPlan.id);
      return fullPlan && !!fullPlan.archivedAt;
    });

    const archivedPrices = originalApiPlans.filter((appPlan) => {
      const fullPlan = fullPlans.find((p) => p.id === appPlan.id);
      if (!fullPlan) return false;
      const selectedPrice = fullPlan.prices.find((p) => p.id === appPlan.priceId);
      return selectedPrice && !!selectedPrice.archivedAt;
    });

    console.log('[ArchivedPlans] Check:', {
      originalApiPlans: originalApiPlans.map((p) => ({ id: p.id, priceId: p.priceId })),
      archivedPlans: archivedPlans.map((p) => ({ id: p.id, priceId: p.priceId })),
      archivedPrices: archivedPrices.map((p) => ({ id: p.id, priceId: p.priceId })),
    });

    if (archivedPlans.length > 0 || archivedPrices.length > 0) {
      console.log('[ArchivedPlans] Found archived plans/prices in application, opening modal');
      set({ isModalOpen: true });
      return true;
    }

    return false;
  },
}));
